import { FormGroup } from '@angular/forms';

/**
 * Utility functions for working with typed reactive forms
 */

/**
 * Gets a typed form control from a FormGroup with full type safety.
 *
 * @example
 * ```typescript
 * interface MyForm {
 *   email: FormControl<string>;
 *   age: FormControl<number>;
 * }
 *
 * const myForm: FormGroup<MyForm> = ...;
 * const emailControl = getTypedControl(myForm, 'email'); // FormControl<string>
 * const ageControl = getTypedControl(myForm, 'age');     // FormControl<number>
 * ```
 *
 * @param formGroup - The typed FormGroup
 * @param controlName - The name of the control to retrieve
 * @returns The typed form control
 */
export function getTypedControl<T extends Record<string, any>, K extends keyof T>(
  formGroup: FormGroup<T>,
  controlName: K
): T[K] | undefined {
  return formGroup?.get(controlName as string) as T[K];
}

/**
 * Gets the value of a typed form control from a FormGroup with full type safety.
 *
 * @example
 * ```typescript
 * interface MyForm {
 *   email: FormControl<string>;
 *   age: FormControl<number>;
 * }
 *
 * const myForm: FormGroup<MyForm> = ...;
 * const emailValue = getTypedControlValue(myForm, 'email'); // string
 * const ageValue = getTypedControlValue(myForm, 'age');     // number
 * ```
 *
 * @param formGroup - The typed FormGroup
 * @param controlName - The name of the control to retrieve the value from
 * @returns The typed value of the form control
 */
export function getTypedControlValue<T extends Record<string, any>, K extends keyof T>(
  formGroup: FormGroup<T>,
  controlName: K
): T[K] extends { value: infer V } ? V | undefined : never {
  return getTypedControl(formGroup, controlName)?.value;
}

/**
 * Type-safe way to get multiple controls from a FormGroup at once.
 *
 * @example
 * ```typescript
 * const { email, age } = getTypedControls(myForm, ['email', 'age']);
 * // email is FormControl<string>, age is FormControl<number>
 * ```
 */
export function getTypedControls<T extends Record<string, any>, K extends keyof T>(
  formGroup: FormGroup<T>,
  controlNames: readonly K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  controlNames.forEach((name) => {
    result[name] = getTypedControl(formGroup, name);
  });
  return result;
}

/**
 * Type-safe way to check if a control exists in a FormGroup.
 *
 * @example
 * ```typescript
 * if (hasControl(myForm, 'email')) {
 *   // TypeScript knows the control exists and is typed
 *   const emailControl = getTypedControl(myForm, 'email');
 * }
 * ```
 */
export function hasControl<T extends Record<string, any>, K extends keyof T>(
  formGroup: FormGroup<T>,
  controlName: K
): boolean {
  return formGroup.contains(controlName as string);
}
